/**
 * Update body composition: a number you have (DEXA, Bod Pod, smart scale...) as body fat % or lean
 * mass, or an estimate from a tape measure (US Navy method). Saved as a weigh-in row so the
 * history, the trend engine and the Body page all see it.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { bodyFatFromLean, CM_PER_IN, leanFromBodyFat, navyBodyFat, type NavyResult } from '../lib/bodyComp';
import { timestampForDay } from '../lib/dates';
import { errorMessage } from '../lib/logger';
import type { BodyStatSource, Sex } from '../lib/types';
import { massFromKg, massToKg, massUnit } from '../lib/units';
import { useApp } from '../state/app';
import { Icon } from './Icon';
import { Modal, Segmented, Spinner } from './ui';

type Tab = 'number' | 'tape';
type Kind = 'bf' | 'lean';

const METHODS: ReadonlyArray<{ value: BodyStatSource; label: string }> = [
  { value: 'dexa', label: 'DEXA scan' },
  { value: 'bod_pod', label: 'Bod Pod' },
  { value: 'bia', label: 'Smart scale or hand-held (BIA)' },
  { value: 'calipers', label: 'Skinfold calipers' },
  { value: 'manual', label: 'Other / I know it' },
];

const numOrNull = (s: string): number | null => { const t = s.trim(); if (t === '') return null; const n = Number(t); return Number.isFinite(n) ? n : null; };
const r1 = (x: number): number => Math.round(x * 10) / 10;

export function BodyCompModal({ initialTab = 'number', onClose }: { initialTab?: Tab; onClose: () => void }): ReactElement {
  const { source, profile, today, energy, toast, bump } = useApp();
  const units = profile.unit_system;
  const imperial: boolean = units === 'imperial';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [date, setDate] = useState<string>(today);
  const [weight, setWeight] = useState<string>(energy.comp.weightKg !== null ? String(r1(massFromKg(energy.comp.weightKg, units))) : '');
  const [kind, setKind] = useState<Kind>('bf');
  const [bf, setBf] = useState<string>('');
  const [lean, setLean] = useState<string>('');
  const [method, setMethod] = useState<BodyStatSource>('dexa');
  const [sex, setSex] = useState<Sex | ''>(profile.sex ?? '');
  const [height, setHeight] = useState<string>(profile.height_cm !== null ? String(r1(imperial ? profile.height_cm / CM_PER_IN : profile.height_cm)) : '');
  const [neck, setNeck] = useState<string>('');
  const [waist, setWaist] = useState<string>('');
  const [hip, setHip] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const wKg: number | null = (() => { const w = numOrNull(weight); return w === null ? null : massToKg(w, units); })();
  const toCm = (s: string): number | null => { const v = numOrNull(s); return v === null ? null : imperial ? v * CM_PER_IN : v; };

  const navy: NavyResult | null = useMemo(() => {
    if (tab !== 'tape' || sex === '') return null;
    const h = toCm(height), n = toCm(neck), w = toCm(waist);
    if (h === null || n === null || w === null) return null;
    return navyBodyFat({ sex, heightCm: h, neckCm: n, waistCm: w, hipCm: toCm(hip) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sex, height, neck, waist, hip, imperial]);

  const bfNum: number | null = numOrNull(bf);
  const leanNum: number | null = numOrNull(lean);
  const derived: string | null = wKg === null ? null
    : kind === 'bf' && bfNum !== null ? `Lean mass ≈ ${r1(massFromKg(leanFromBodyFat(wKg, bfNum), units))} ${massUnit(units)}`
    : kind === 'lean' && leanNum !== null ? `Body fat ≈ ${r1(bodyFatFromLean(wKg, massToKg(leanNum, units)))}%`
    : null;

  const save = async (): Promise<void> => {
    setError(null);
    if (wKg === null || wKg < 20 || wKg > 400) { setError(`Enter your weight at the time of the measurement (${massUnit(units)}).`); return; }
    if (date > today) { setError('The date can’t be in the future.'); return; }
    let bodyFat: number | null = null;
    let leanKg: number | null = null;
    let src: BodyStatSource = method;
    let notes: string | null = null;
    if (tab === 'tape') {
      if (!navy || !navy.ok) { setError(navy && !navy.ok ? navy.reason : 'Fill in the measurements first.'); return; }
      bodyFat = navy.bodyFatPct;
      src = 'estimate';
      const u: string = imperial ? 'in' : 'cm';
      notes = `US Navy tape: neck ${neck} ${u}, ${sex === 'male' ? 'abdomen' : 'waist'} ${waist} ${u}${sex === 'female' ? `, hips ${hip} ${u}` : ''}, height ${height} ${u}.`;
    } else if (kind === 'bf') {
      if (bfNum === null || bfNum < 2 || bfNum > 75) { setError('Body fat must be between 2 and 75%.'); return; }
      bodyFat = r1(bfNum);
    } else {
      if (leanNum === null) { setError(`Enter your lean mass in ${massUnit(units)}.`); return; }
      leanKg = Math.round(massToKg(leanNum, units) * 100) / 100;
      if (leanKg < 10 || leanKg > 250 || leanKg >= wKg) { setError('Lean mass must be less than your weight (and between 10 and 250 kg).'); return; }
      // Stored with its body-fat % so the trend engine (which reads body fat) sees the same thing.
      bodyFat = r1(bodyFatFromLean(wKg, leanKg));
      if (bodyFat < 2 || bodyFat > 75) { setError('That lean mass works out to an impossible body fat %. Check the numbers.'); return; }
    }
    setBusy(true);
    try {
      await source.insertBodyStat({
        measured_at: date === today ? new Date().toISOString() : timestampForDay(date, 12),
        local_date: date, weight_kg: Math.round(wKg * 100) / 100, body_fat_pct: bodyFat, lean_mass_kg: leanKg,
        source: src, is_fasted: null, notes,
      });
      toast(`Saved: ${bodyFat}% body fat`);
      bump();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const u: string = imperial ? 'in' : 'cm';
  const field = (label: string, value: string, set: (v: string) => void, suffix: string, hint?: string): ReactElement => (
    <label className="field"><span>{label}</span>
      <div className="input-suffix"><input className="input" type="number" inputMode="decimal" step="0.1" min={0} value={value} onChange={(e) => set(e.target.value)} /><em>{suffix}</em></div>
      {hint && <small>{hint}</small>}
    </label>
  );

  return (
    <Modal title="Body composition" onClose={onClose} width={620} footer={
      <>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? <Spinner /> : <Icon name="check" size={18} />} Save</button>
      </>
    }>
      <div className="stack" style={{ gap: 16 }}>
        <div className="tabs" role="tablist">
          <button type="button" className={`tab ${tab === 'number' ? 'on' : ''}`} onClick={() => setTab('number')}>I have a number</button>
          <button type="button" className={`tab ${tab === 'tape' ? 'on' : ''}`} onClick={() => setTab('tape')}>Tape measure (Navy method)</button>
        </div>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <label className="field"><span>Date measured</span><input className="input" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} /></label>
          {field('Your weight then', weight, setWeight, massUnit(units))}
        </div>

        {tab === 'number' ? (
          <>
            <Segmented<Kind> options={[{ value: 'bf', label: 'Body fat %' }, { value: 'lean', label: 'Lean mass' }]} value={kind} onChange={setKind} />
            <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {kind === 'bf' ? field('Body fat', bf, setBf, '%') : field('Lean body mass', lean, setLean, massUnit(units), 'Fat-free mass from your scan report.')}
              <label className="field"><span>Measured with</span>
                <select className="input" value={method} onChange={(e) => setMethod(e.target.value as BodyStatSource)}>
                  {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
            </div>
            {derived && <div className="note info">{derived}</div>}
          </>
        ) : (
          <>
            {profile.sex === null && (
              <label className="field"><span>Sex (the equations differ)</span>
                <select className="input" value={sex} onChange={(e) => setSex(e.target.value as Sex | '')}><option value="">—</option><option value="male">Male</option><option value="female">Female</option></select>
              </label>
            )}
            <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {field('Height', height, setHeight, u)}
              {field('Neck', neck, setNeck, u, 'Just below the Adam’s apple, tape sloping slightly down to the front.')}
              {field(sex === 'female' ? 'Waist' : 'Waist at the navel', waist, setWaist, u, sex === 'female' ? 'At the narrowest point.' : 'Level with the belly button, relaxed, after breathing out.')}
              {sex === 'female' && field('Hips', hip, setHip, u, 'At the widest point of the buttocks.')}
            </div>
            {navy?.ok && <div className="note info"><b>≈ {navy.bodyFatPct}% body fat</b>{wKg !== null ? ` · lean mass ≈ ${r1(massFromKg(leanFromBodyFat(wKg, navy.bodyFatPct), units))} ${massUnit(units)}` : ''}</div>}
            {navy && !navy.ok && <div className="note warn">{navy.reason}</div>}
            <p className="cite" style={{ margin: 0 }}>
              US Navy circumference equations (Hodgdon &amp; Beckett, Naval Health Research Center 1984; the form in DoD Instruction 1308.3). Measure each site three times on bare skin, tape level and snug but not pressing, and use the average. Against a DEXA scan a tape estimate is often 4-6 points off for one person (McClung 2025, Int J Obes; Potter 2022, Front Physiol), so use a scan number when you have one.
            </p>
          </>
        )}
        {error !== null && <div className="note err">{error}</div>}
      </div>
    </Modal>
  );
}
